import React, { useState } from 'react';
import { MarkerClusterer } from '@react-google-maps/api';
import { MapMarker } from '../MapMarker';
import { POIForm } from '../../poi/POIForm';
import { logError, logInfo } from '../../../../../utils/logger';

export const POILayer = ({ pois = [], visibilityState = {}, map, onDriveTo, onDelete, settings }) => {
  const [editingPOI, setEditingPOI] = useState(null);

  const getMarkerSize = (zoomLevel) => {
    const sizes = settings?.poiSizes || { far: 32, medium: 32, close: 32 };
    if (zoomLevel <= 7) return sizes.far;
    if (zoomLevel <= 14) return sizes.medium;
    return sizes.close;
  };

  const getClusterSize = (zoomLevel) => {
    const sizes = settings?.clusterSizes || { far: 40, medium: 40, close: 40 };
    if (zoomLevel <= 7) return sizes.far;
    if (zoomLevel <= 14) return sizes.medium;
    return sizes.close;
  };

  // Filter POIs based on visibility state
  const visiblePOIs = pois.filter(poi => {
    return !poi.deleted && // Not deleted
           visibilityState.poiTypes?.[poi.type] !== false && // Type is visible
           visibilityState.pois?.[poi.id] !== false && // Individual POI is visible
           poi.coordinates?.latitude != null && // Has valid coordinates
           poi.coordinates?.longitude != null;
  });

  logInfo(`Rendering ${visiblePOIs.length} visible POIs`);

  return (
    <>
      {visiblePOIs.length > 0 && (
        <MarkerClusterer
          averageCenter={true}
          maxZoom={14}
          minimumClusterSize={3}
          gridSize={60}
          zoomOnClick={true}
          calculator={(markers, numStyles) => {
            const count = markers.length;
            const zoomLevel = map.getZoom();
            const size = getClusterSize(zoomLevel);
            return {
              text: count.toString(),
              index: Math.min(numStyles, count),
              title: `${count} POIs`,
              size
            };
          }}
          styles={[{
            textColor: 'white',
            height: settings?.clusterSizes?.medium || 40,
            width: settings?.clusterSizes?.medium || 40,
            url: `data:image/svg+xml;base64,${btoa(`
              <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="18" fill="#666666" stroke="white" stroke-width="3"/>
              </svg>
            `)}`,
            textSize: 14,
            textAlign: 'center',
            textLineHeight: 40,
            fontFamily: 'Arial',
            fontWeight: 'bold'
          }]}
        >
          {(clusterer) => (
            visiblePOIs.map(poi => {
              // Validate coordinates
              const lat = Number(poi.coordinates.latitude);
              const lng = Number(poi.coordinates.longitude);
              
              if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
                logError(`Invalid coordinates for POI ${poi.id}:`, poi.coordinates);
                return null;
              }

              return (
                <MapMarker
                  key={poi.id}
                  poi={poi}
                  clusterer={clusterer}
                  onEdit={setEditingPOI}
                  onDriveTo={onDriveTo}
                  onDelete={onDelete}
                  map={map}
                  size={getMarkerSize(map.getZoom())}
                />
              );
            })
          )}
        </MarkerClusterer>
      )}

      {/* POI Edit Form */}
      {editingPOI && (
        <POIForm
          type={editingPOI.type}
          coordinates={editingPOI.coordinates}
          initialData={editingPOI}
          onClose={() => setEditingPOI(null)}
        />
      )}
    </>
  );
};

export default POILayer;